/**
 * Session state.
 *
 * The access token lives in a ref here — in memory, never in storage — and is
 * handed to the HTTP client through `setAuthHelpers` rather than imported, so
 * `api/client.ts` stays free of React and independently testable.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { setAuthHelpers } from '@/api/client';
import type { CurrentUser } from '@/api/types';

import { authApi, type LoginPayload } from './api/authApi';
import { restoreSession } from './api/restore';
import { AuthContext, type AuthContextValue } from './authContext';

export function AuthProvider({ children }: { children: ReactNode }) {
  const accessToken = useRef<string | null>(null);
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isRestoring, setIsRestoring] = useState(true);

  const clearSession = useCallback(() => {
    accessToken.current = null;
    setUser(null);
  }, []);

  // Wire the HTTP client once, before anything can issue a request.
  useEffect(() => {
    setAuthHelpers({
      getToken: () => accessToken.current,
      onUnauthorized: clearSession,
      onTokenRefreshed: (token) => {
        accessToken.current = token;
      },
    });
  }, [clearSession]);

  // Restore on load: the in-memory token did not survive the refresh, but the
  // HttpOnly cookie may have. `restoreSession` deduplicates the call across
  // the dev-mode StrictMode double-mount — without it, two `/auth/me/`
  // requests fire per page load (issue #18).
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const restored = await restoreSession();
      if (cancelled) return;
      if (restored) setUser(restored);
      else clearSession();
      setIsRestoring(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [clearSession]);

  const login = useCallback(async (payload: LoginPayload) => {
    const { access, user: loggedIn } = await authApi.login(payload);
    accessToken.current = access;
    setUser(loggedIn);
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      // Clear locally even if the call failed: the user asked to leave, and a
      // session that survives a failed logout is the worse outcome.
      clearSession();
    }
  }, [clearSession]);

  const refreshUser = useCallback(async () => {
    setUser(await authApi.me());
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isRestoring,
      login,
      logout,
      refreshUser,
    }),
    [user, isRestoring, login, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
